import { useState } from 'react';

export default function ConstructionManagementSection() {
  const [activeTab, setActiveTab] = useState<'construction' | 'management'>('construction');

  const savingsMetrics = [
    { icon: 'ri-time-line', value: '40%', label: 'Average Time Saved', description: 'On project coordination and vendor sourcing' },
    { icon: 'ri-money-dollar-circle-line', value: '25-35%', label: 'Cost Reduction', description: 'Through vetted vendor pricing and waste elimination' },
    { icon: 'ri-shield-check-line', value: '100%', label: 'Accountability', description: 'Every vendor verified, every dollar tracked' },
  ];

  const vendorCategories = [
    { icon: 'ri-hammer-line', title: 'General Contractors', description: 'Licensed, insured, and performance verified contractors for projects of any scale.' },
    { icon: 'ri-ruler-line', title: 'Engineers & Architects', description: 'Structural, civil, and design professionals for code compliance and planning.' },
    { icon: 'ri-flashlight-line', title: 'Electrical & Lighting', description: 'Certified electricians specializing in security lighting, panels, and smart systems.' },
    { icon: 'ri-drop-line', title: 'Plumbing & Waterproofing', description: 'Leak detection, drainage solutions, and water intrusion prevention specialists.' },
    { icon: 'ri-paint-brush-line', title: 'Painting & Restoration', description: 'Interior/exterior coatings, pressure washing, and surface restoration.' },
    { icon: 'ri-landscape-line', title: 'Landscaping & Hardscape', description: 'Grounds maintenance, pavers, fencing, and perimeter improvements.' },
    { icon: 'ri-door-lock-line', title: 'Access & Security Install', description: 'Gate systems, locks, intercoms, and surveillance hardware installation.' },
    { icon: 'ri-home-gear-line', title: 'Roofing & Envelope', description: 'Roof inspections, repairs, replacements, and building envelope integrity.' },
  ];

  const managementPillars = [
    {
      icon: 'ri-line-chart-line',
      title: 'Budget Planning & Control',
      description: 'We help you build realistic maintenance and capital improvement budgets, then hold vendors accountable to them. No surprise invoices, no scope creep.',
      points: ['Annual maintenance budgeting', 'Capital reserve planning', 'Cost-benefit analysis on repairs vs. replacement', 'Invoice verification and audit']
    },
    {
      icon: 'ri-calendar-check-line',
      title: 'Preventative Scheduling',
      description: 'Proactive maintenance calendars that catch small problems before they become expensive emergencies. We schedule, coordinate, and verify completion.',
      points: ['Seasonal maintenance calendars', 'Equipment lifecycle tracking', 'Warranty expiration monitoring', 'Recurring inspection schedules']
    },
    {
      icon: 'ri-file-search-line',
      title: 'Project Oversight & Quality Control',
      description: 'From bid review to final walkthrough, SafePoint acts as your on-site representative ensuring work meets specifications, timelines, and budget.',
      points: ['Bid comparison and review', 'On-site progress inspections', 'Punch list management', 'Final quality verification']
    },
    {
      icon: 'ri-shield-user-line',
      title: 'Vendor Accountability',
      description: 'Every vendor in our network is vetted for licensing, insurance, references, and performance history. We hold them to the same standards we hold ourselves.',
      points: ['License and insurance verification', 'Reference and performance checks', 'Ongoing quality monitoring', 'Single point of contact coordination']
    },
  ];

  return (
    <section className="py-20 bg-slate-900 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="inline-block px-4 py-1 bg-amber-500/20 text-amber-400 rounded-full text-sm font-medium mb-4">
            Construction & Property Management
          </span>
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Saving You Time & Money on Every Project
          </h2>
          <p className="text-lg text-slate-300 max-w-3xl mx-auto">
            SafePoint bridges the gap between property owners and the trades. Our construction background and vetted vendor network 
            eliminate guesswork, prevent overspending, and keep every project on track.
          </p>
        </div>

        {/* Savings Metrics */}
        <div className="grid md:grid-cols-3 gap-6 mb-16">
          {savingsMetrics.map((metric, index) => (
            <div key={index} className="bg-gradient-to-br from-amber-600/20 to-slate-800/60 rounded-2xl p-8 border border-amber-500/20 text-center hover:border-amber-500/40 transition-all duration-300">
              <div className="w-14 h-14 flex items-center justify-center bg-amber-500/20 rounded-xl mx-auto mb-4">
                <i className={`${metric.icon} text-2xl text-amber-400`}></i>
              </div>
              <div className="text-4xl font-bold text-amber-400 mb-2">{metric.value}</div>
              <div className="text-lg font-semibold text-white mb-1">{metric.label}</div>
              <p className="text-sm text-slate-400">{metric.description}</p>
            </div>
          ))}
        </div>

        {/* Tab Toggle */}
        <div className="flex justify-center mb-12">
          <div className="inline-flex bg-slate-800 rounded-full p-1">
            <button
              onClick={() => setActiveTab('construction')}
              className={`px-6 py-3 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                activeTab === 'construction'
                  ? 'bg-amber-500 text-white'
                  : 'text-slate-300 hover:text-white'
              }`}
            >
              Vetted Vendors & Construction
            </button>
            <button
              onClick={() => setActiveTab('management')}
              className={`px-6 py-3 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                activeTab === 'management'
                  ? 'bg-amber-500 text-white'
                  : 'text-slate-300 hover:text-white'
              }`}
            >
              Property & Project Management
            </button>
          </div>
        </div>

        {/* Construction / Vetted Vendors Tab */}
        {activeTab === 'construction' && (
          <div>
            <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
              <div>
                <div className="bg-slate-800/50 rounded-2xl p-8 border border-slate-700">
                  <div className="w-14 h-14 flex items-center justify-center bg-amber-500/20 rounded-xl mb-6">
                    <i className="ri-hammer-line text-2xl text-amber-400"></i>
                  </div>
                  <h3 className="text-2xl font-bold mb-4">Why Vetted Vendors Matter</h3>
                  <p className="text-slate-300 mb-6 leading-relaxed">
                    Hiring the wrong contractor costs more than money it costs time, safety, and peace of mind. 
                    Property owners often overpay for underqualified work or waste weeks chasing unreliable vendors. 
                    SafePoint eliminates that risk entirely.
                  </p>
                  <h4 className="text-lg font-semibold text-amber-400 mb-3">The SafePoint Difference:</h4>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-3">
                      <i className="ri-checkbox-circle-fill text-amber-400 mt-1"></i>
                      <span className="text-slate-300">Every vendor is pre-screened for licensing, insurance, and track record</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <i className="ri-checkbox-circle-fill text-amber-400 mt-1"></i>
                      <span className="text-slate-300">Competitive pricing negotiated through established relationships</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <i className="ri-checkbox-circle-fill text-amber-400 mt-1"></i>
                      <span className="text-slate-300">One point of contact manages all trades no chasing multiple contractors</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <i className="ri-checkbox-circle-fill text-amber-400 mt-1"></i>
                      <span className="text-slate-300">Construction knowledge ensures you never pay for unnecessary work</span>
                    </li>
                  </ul>
                </div>
              </div>
              <div className="relative">
                <div className="rounded-2xl overflow-hidden h-96">
                  <img
                    src="https://public.readdy.ai/ai/img_res/71b2c9ceaf70f0a51e7ecd767d464dee.jpg"
                    alt="Professional construction team reviewing plans on site"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 to-transparent rounded-2xl"></div>
                <div className="absolute bottom-6 left-6 right-6">
                  <p className="text-sm text-slate-300 italic">
                    &quot;The right vendor at the right price—every time. That&apos;s the SafePoint standard.&quot;
                  </p>
                </div>
              </div>
            </div>

            {/* Vendor Network Grid */}
            <div className="mb-8">
              <h3 className="text-2xl font-bold text-center mb-3">Our Vetted Vendor Network</h3>
              <p className="text-slate-400 text-center mb-10 max-w-2xl mx-auto">
                Every trade, one trusted point of contact. All vendors are verified, insured, and held to SafePoint&apos;s performance standards.
              </p>
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {vendorCategories.map((vendor, index) => (
                  <div key={index} className="bg-slate-800/50 rounded-xl p-5 border border-slate-700 hover:border-amber-500/40 transition-all duration-300 group cursor-pointer">
                    <div className="w-11 h-11 flex items-center justify-center bg-amber-500/20 rounded-lg mb-4 group-hover:bg-amber-500/30 transition-colors">
                      <i className={`${vendor.icon} text-xl text-amber-400`}></i>
                    </div>
                    <h4 className="font-semibold text-white text-sm mb-2">{vendor.title}</h4>
                    <p className="text-xs text-slate-400 leading-relaxed">{vendor.description}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Property / Project Management Tab */}
        {activeTab === 'management' && (
          <div>
            <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
              <div className="relative order-2 lg:order-1">
                <div className="rounded-2xl overflow-hidden h-96">
                  <img
                    src="https://readdy.ai/api/search-image?query=Professional%20property%20manager%20inspecting%20modern%20commercial%20building%20interior%20with%20clipboard%20tablet%20checking%20maintenance%20systems%20clean%20well%20maintained%20facility%20bright%20natural%20lighting%20corporate%20professional%20photography%20style&width=600&height=400&seq=mgmt1&orientation=landscape"
                    alt="Property manager conducting building inspection"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 to-transparent rounded-2xl"></div>
                <div className="absolute bottom-6 left-6 right-6">
                  <p className="text-sm text-slate-300 italic">
                    &quot;Proactive management today prevents costly emergencies tomorrow.&quot;
                  </p>
                </div>
              </div>
              <div className="order-1 lg:order-2">
                <div className="bg-slate-800/50 rounded-2xl p-8 border border-slate-700">
                  <div className="w-14 h-14 flex items-center justify-center bg-amber-500/20 rounded-xl mb-6">
                    <i className="ri-building-line text-2xl text-amber-400"></i>
                  </div>
                  <h3 className="text-2xl font-bold mb-4">Property & Project Management That Pays for Itself</h3>
                  <p className="text-slate-300 mb-6 leading-relaxed">
                    Most property owners don't have the time, expertise, or vendor relationships to manage maintenance and capital projects efficiently. 
                    They end up overpaying, missing critical maintenance windows, or dealing with avoidable emergencies.
                  </p>
                  <h4 className="text-lg font-semibold text-amber-400 mb-3">SafePoint Changes That:</h4>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-3">
                      <i className="ri-checkbox-circle-fill text-amber-400 mt-1"></i>
                      <span className="text-slate-300">We act as your dedicated project manager from planning through completion</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <i className="ri-checkbox-circle-fill text-amber-400 mt-1"></i>
                      <span className="text-slate-300">Preventative maintenance schedules that extend asset life and reduce costs</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <i className="ri-checkbox-circle-fill text-amber-400 mt-1"></i>
                      <span className="text-slate-300">Budget oversight ensures every dollar is spent wisely and accounted for</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <i className="ri-checkbox-circle-fill text-amber-400 mt-1"></i>
                      <span className="text-slate-300">Quality control inspections at every phase no cutting corners</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Management Pillars */}
            <div className="grid md:grid-cols-2 gap-6">
              {managementPillars.map((pillar, index) => (
                <div key={index} className="bg-gradient-to-br from-slate-800/80 to-slate-800/40 rounded-2xl p-8 border border-slate-700 hover:border-amber-500/30 transition-all duration-300">
                  <div className="flex items-start gap-5">
                    <div className="w-12 h-12 flex items-center justify-center bg-amber-500/20 rounded-xl flex-shrink-0">
                      <i className={`${pillar.icon} text-xl text-amber-400`}></i>
                    </div>
                    <div>
                      <h4 className="text-lg font-bold text-white mb-2">{pillar.title}</h4>
                      <p className="text-sm text-slate-300 mb-4 leading-relaxed">{pillar.description}</p>
                      <ul className="space-y-2">
                        {pillar.points.map((point, idx) => (
                          <li key={idx} className="flex items-center gap-2">
                            <i className="ri-arrow-right-s-line text-amber-400 text-sm"></i>
                            <span className="text-xs text-slate-400">{point}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* How It All Comes Together */}
        <div className="bg-gradient-to-br from-amber-900/30 to-slate-800/40 rounded-3xl p-8 md:p-12 border border-amber-700/30 mt-16">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-amber-500/20 rounded-full mb-6">
                <i className="ri-funds-line text-amber-400"></i>
                <span className="text-sm font-medium text-amber-400">The Bottom Line</span>
              </div>
              <h3 className="text-2xl md:text-3xl font-bold mb-4">
                One Call. One Team. Complete Coverage.
              </h3>
              <p className="text-slate-300 mb-8 leading-relaxed">
                Instead of juggling multiple contractors, property managers, and consultants SafePoint coordinates everything 
                under one roof. Our construction expertise means we speak the language of every trade, and our management 
                approach ensures nothing falls through the cracks.
              </p>

              <div className="space-y-4">
                <div className="flex items-center gap-4 bg-slate-800/50 rounded-xl p-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-red-500/20 rounded-lg flex-shrink-0">
                    <i className="ri-close-circle-line text-xl text-red-400"></i>
                  </div>
                  <div>
                    <h5 className="font-medium text-white text-sm">Without SafePoint</h5>
                    <p className="text-xs text-slate-400 mt-1">Multiple vendors, no oversight, surprise costs, missed deadlines, finger-pointing when things go wrong</p>
                  </div>
                </div>
                <div className="flex items-center gap-4 bg-slate-800/50 rounded-xl p-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-emerald-500/20 rounded-lg flex-shrink-0">
                    <i className="ri-checkbox-circle-line text-xl text-emerald-400"></i>
                  </div>
                  <div>
                    <h5 className="font-medium text-white text-sm">With SafePoint</h5>
                    <p className="text-xs text-slate-400 mt-1">Vetted vendors, active oversight, transparent budgets, on time delivery, single point of accountability</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="relative">
              <div className="rounded-2xl overflow-hidden h-80 lg:h-96">
                <img
                  src="https://readdy.ai/api/search-image?query=Beautiful%20modern%20luxury%20residential%20property%20exterior%20with%20manicured%20landscaping%20new%20construction%20quality%20finishes%20stone%20and%20stucco%20facade%20professional%20real%20estate%20photography%20golden%20hour%20warm%20lighting%20showing%20completed%20renovation%20project%20pristine%20condition&width=600&height=450&seq=const3&orientation=landscape"
                  alt="Completed property renovation project"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 bg-slate-800 rounded-xl p-5 border border-slate-700 shadow-xl max-w-xs">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 flex items-center justify-center bg-amber-500 rounded-lg">
                    <i className="ri-hand-coin-line text-white text-lg"></i>
                  </div>
                  <span className="font-semibold text-white">Save Time & Money</span>
                </div>
                <p className="text-sm text-slate-400">
                  From first call to final walkthrough one team, one budget, zero surprises.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom CTA */}
        <div className="text-center mt-16">
          <p className="text-slate-400 mb-6">
            Ready to stop overpaying and start managing your property the smart way?
          </p>
          <a
            href="#contact"
            className="inline-flex items-center gap-2 px-8 py-4 bg-amber-500 hover:bg-amber-600 text-white font-semibold rounded-lg transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-phone-line"></i>
            Get a Free Consultation
          </a>
        </div>
      </div>
    </section>
  );
}
